import 'moment-timezone';
import { ErrorMessage, Form, Formik } from 'formik';
import { useAtom } from 'jotai';
import moment from 'moment';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { userAtom } from '../jotai-atoms/user.atom';
import { InputFeedback } from '../stories/InputFeedback';
import { Modal } from '../stories/Modal';
import { Panel } from '../stories/Panel';
import SelectInput, { SelectInputOption } from '../stories/SelectInput';
import { getRecommendedTimezoneOption } from '../utils';
import { useUpdateAccountInformation } from '../hooks/use-update-account-information';

interface ModalTimezoneProps {
  show: boolean;
  onClose: () => void;
  csrftoken: string;
  callback: null | (() => void);
}

export const ModalTimezone: FC<ModalTimezoneProps> = ({ show, onClose, csrftoken, callback }) => {
  const __ = useTranslation().t;

  const [errorStateActive, setErrorStateActive] = useState<boolean>(false);

  const { mutateAsync, isLoading } = useUpdateAccountInformation();
  const [user, setUser] = useAtom(userAtom);

  const initialValues = {
    timezoneOption: {
      label: __('setTimeZone'),
      value: '',
    },
  };

  const schema = yup.object().shape({
    timezoneOption: yup.object().nullable().required(__('fieldRequired')),
  });

  const modalProps = {
    show,
    type: 'default',
    title: __('setTimeZone'),
    submitButtonText: __('save'),
  };

  const onSubmit = (values: { timezoneOption: any }) => {
    if (!user) {
      return;
    }

    const accountInformationWithToken = {
      timezone: values.timezoneOption?.value as string,
      language: user.lang,
      _token: csrftoken,
    };
    mutateAsync(accountInformationWithToken, {
      onSuccess: () => {
        setErrorStateActive(false);
        onClose();
        const timezone = values.timezoneOption?.value;
        setUser({ ...user, timezone });
        localStorage.setItem('today', moment().tz(timezone).format('YYYY-MM-DD'));
        callback && callback();
      },
      onError: () => {
        setErrorStateActive(true);
      }
    });
  };

  const handleClose = (resetForm: () => void) => {
    resetForm();
    setErrorStateActive(false);
    onClose();
  };

  const timezoneOptions = Object.keys(user?.timezoneOptions || {}).map((key: string) => {
    const label = user?.timezoneOptions && user?.timezoneOptions[key];
    return {
      label,
      value: key,
    } as SelectInputOption;
  });

  const recommendedOption = getRecommendedTimezoneOption(user?.timezoneOptions);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {({ isSubmitting, values, setFieldValue, submitForm, resetForm }) => (
        <Modal
          {...modalProps}
          submitButtonDisabled={isLoading || !values.timezoneOption?.value}
          onClose={() => handleClose(resetForm)}
          onSubmit={submitForm}
        >
          <Form className="oejr-profile-modal-form">
            <Panel
              variant="danger"
              hidden={!errorStateActive}
              onHide={() => setErrorStateActive(false)}
            >
              <p>{__('opsCheckTyposContactUs')}</p>
            </Panel>
            <SelectInput
              label={__('timezone')}
              name="timezoneOption"
              options={timezoneOptions}
              value={values.timezoneOption}
              onChange={(option) => setFieldValue('timezoneOption', option)}
              searchable
              recommendedOption={recommendedOption}
            />
            <ErrorMessage
              name="timezoneOption"
              component={({ children }: any) => (
                <InputFeedback state="error" text={children} icon="exclamation-circle" />
              )}
            />
          </Form>
        </Modal>
      )}
    </Formik>
  );
};
